/**
 * NAS 同步控制面板
 *
 * 功能：
 * - 顯示 NAS 連線狀態
 * - 觸發手動同步
 * - 即時顯示同步進度（每 2s 輪詢）
 * - 顯示歷史同步紀錄
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  HardDrive,
  RefreshCw,
  CheckCircle,
  XCircle,
  Loader,
  ChevronDown,
  ChevronUp,
  Wifi,
  WifiOff,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { apiService } from '../services/api';

interface SyncStats {
  scanned: number;
  added: number;
  updated: number;
  deleted: number;
  skipped: number;
  errors: number;
}

interface SyncProgress {
  id: string;
  triggeredBy: string;
  rootPaths: string[];
  startedAt: string;
  status: 'running' | 'completed' | 'failed';
  stats: SyncStats;
  currentFile?: string;
  errorMessage?: string;
}

interface HistoryRun {
  id: string;
  triggeredBy: string;
  rootPaths: string[];
  startedAt: string;
  completedAt?: string;
  status: string;
  stats: SyncStats;
  errorMessage?: string;
}

interface NasSyncPanelProps {
  onSyncComplete?: () => void;
}

const NasSyncPanel: React.FC<NasSyncPanelProps> = ({ onSyncComplete }) => {
  const [expanded, setExpanded] = useState(false);
  const [nasReachable, setNasReachable] = useState<boolean | null>(null);
  const [nasHost, setNasHost] = useState('');
  const [progress, setProgress] = useState<SyncProgress | null>(null);
  const [history, setHistory] = useState<HistoryRun[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Ping NAS ──────────────────────────────────────────────────────────
  const pingNas = useCallback(async () => {
    try {
      const result = await apiService.pingNas();
      setNasReachable(result.reachable);
      setNasHost(result.nasHost);
    } catch {
      setNasReachable(false);
    }
  }, []);

  // ─── Poll current sync ─────────────────────────────────────────────────
  const pollProgress = useCallback(async () => {
    try {
      const current = await apiService.getNasSyncCurrent();
      setProgress(current);
      if (current?.status !== 'running') {
        stopPoll();
        setSyncing(false);
        if (current?.status === 'completed') {
          onSyncComplete?.();
          loadHistory();
        }
      }
    } catch {
      /* ignore polling errors */
    }
  }, [onSyncComplete]);

  const startPoll = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(pollProgress, 2000);
  }, [pollProgress]);

  const stopPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // ─── Load history ───────────────────────────────────────────────────────
  const loadHistory = async () => {
    try {
      const runs = await apiService.getNasSyncHistory(10);
      setHistory(runs);
    } catch {
      /* ignore */
    }
  };

  // ─── Init ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (expanded) {
      pingNas();
      pollProgress();
      loadHistory();
    }
  }, [expanded, pingNas, pollProgress]);

  useEffect(() => {
    return () => stopPoll();
  }, []);

  // ─── Trigger sync ───────────────────────────────────────────────────────
  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const run = await apiService.triggerNasSync();
      setProgress(run as any);
      startPoll();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        // Already running — just start polling
        startPoll();
      } else {
        setSyncing(false);
        alert('無法啟動同步：' + (err?.response?.data?.error || err.message));
      }
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────
  const formatTime = (iso: string) =>
    new Date(iso).toLocaleString('zh-TW', { dateStyle: 'short', timeStyle: 'short' });

  const statusColor = (status: string) => {
    if (status === 'completed') return 'text-green-600';
    if (status === 'failed') return 'text-red-500';
    if (status === 'running') return 'text-blue-600';
    return 'text-slate-500';
  };

  const isRunning = progress?.status === 'running' || syncing;
  const doneCount = progress
    ? progress.stats.added + progress.stats.updated + progress.stats.skipped + progress.stats.errors
    : 0;
  const pct =
    progress && progress.stats.scanned > 0
      ? Math.round((doneCount / progress.stats.scanned) * 100)
      : 0;

  return (
    <div className="p-4 bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl border border-violet-100">
      {/* Header row */}
      <button
        className="w-full flex items-center justify-between"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-2">
          <HardDrive size={16} className="text-violet-600" />
          <h3 className="font-black text-xs text-violet-900">NAS 同步</h3>
          {isRunning && (
            <span className="ml-1 text-[10px] font-bold text-blue-600 flex items-center gap-1">
              <Loader size={10} className="animate-spin" /> 同步中
            </span>
          )}
          {progress?.status === 'completed' && !isRunning && (
            <CheckCircle size={12} className="text-green-500 ml-1" />
          )}
          {progress?.status === 'failed' && !isRunning && (
            <XCircle size={12} className="text-red-500 ml-1" />
          )}
        </div>
        {expanded ? <ChevronUp size={14} className="text-violet-400" /> : <ChevronDown size={14} className="text-violet-400" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {/* NAS connection status */}
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-slate-500 font-bold truncate max-w-[140px]" title={nasHost}>
              {nasHost || '未設定'}
            </span>
            <span className={`flex items-center gap-1 font-bold ${nasReachable === null ? 'text-slate-400' : nasReachable ? 'text-green-600' : 'text-red-500'}`}>
              {nasReachable === null ? (
                <Loader size={10} className="animate-spin" />
              ) : nasReachable ? (
                <><Wifi size={11} /> 已連線</>
              ) : (
                <><WifiOff size={11} /> 無法連線</>
              )}
            </span>
          </div>

          {/* Sync trigger button */}
          <button
            onClick={handleSync}
            disabled={isRunning}
            className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[11px] font-black transition-all
              ${isRunning
                ? 'bg-violet-100 text-violet-400 cursor-not-allowed'
                : 'bg-violet-600 text-white hover:bg-violet-700 shadow-md shadow-violet-100'
              }`}
          >
            <RefreshCw size={12} className={isRunning ? 'animate-spin' : ''} />
            {isRunning ? '同步中...' : '立即同步'}
          </button>

          {/* Progress bar */}
          {isRunning && progress && progress.stats.scanned > 0 && (
            <div>
              <div className="flex justify-between text-[10px] text-slate-500 font-bold mb-1">
                <span>{doneCount} / {progress.stats.scanned}</span>
                <span>{pct}%</span>
              </div>
              <div className="h-1.5 bg-white/60 rounded-full overflow-hidden">
                <div
                  className="h-full bg-violet-500 rounded-full transition-all duration-300"
                  style={{ width: `${pct}%` }}
                />
              </div>
              {progress.currentFile && (
                <p className="text-[10px] text-slate-400 truncate mt-1" title={progress.currentFile}>
                  {progress.currentFile.split('/').pop()}
                </p>
              )}
            </div>
          )}

          {/* Last sync stats */}
          {progress && !isRunning && (
            <div className="grid grid-cols-3 gap-1">
              {[
                { label: '新增', value: progress.stats.added, color: 'text-green-600' },
                { label: '更新', value: progress.stats.updated, color: 'text-blue-600' },
                { label: '刪除', value: progress.stats.deleted, color: 'text-orange-500' },
                { label: '略過', value: progress.stats.skipped, color: 'text-slate-500' },
                { label: '掃描', value: progress.stats.scanned, color: 'text-violet-600' },
                { label: '錯誤', value: progress.stats.errors, color: 'text-red-500' },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-white/60 rounded-lg p-1.5 text-center">
                  <div className={`text-sm font-black ${color}`}>{value}</div>
                  <div className="text-[9px] text-slate-400 font-bold">{label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Error message */}
          {progress?.errorMessage && (
            <div className="flex items-start gap-1.5 p-2 bg-red-50 rounded-lg">
              <AlertTriangle size={12} className="text-red-500 mt-0.5 shrink-0" />
              <p className="text-[10px] text-red-600 font-bold">{progress.errorMessage}</p>
            </div>
          )}

          {/* History toggle */}
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="w-full flex items-center justify-center gap-1.5 text-[10px] text-violet-500 font-bold hover:text-violet-700 transition-colors"
          >
            <Clock size={10} />
            {showHistory ? '隱藏歷史' : '查看同步歷史'}
          </button>

          {showHistory && (
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
              {history.length === 0 && (
                <p className="text-[10px] text-slate-400 font-bold text-center py-2">無同步記錄</p>
              )}
              {history.map((run) => (
                <div key={run.id} className="bg-white/70 rounded-xl p-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-black ${statusColor(run.status)}`}>
                      {run.status === 'completed' ? '✓ 完成' : run.status === 'failed' ? '✗ 失敗' : '⟳ 進行中'}
                    </span>
                    <span className="text-[9px] text-slate-400 font-bold">
                      {formatTime(run.startedAt)}
                    </span>
                  </div>
                  <div className="flex gap-2 text-[9px] text-slate-500 font-bold">
                    <span>+{run.stats?.added ?? 0}</span>
                    <span>↻{run.stats?.updated ?? 0}</span>
                    <span>✗{run.stats?.deleted ?? 0}</span>
                    <span className="ml-auto">{run.stats?.scanned ?? 0} 掃描</span>
                  </div>
                  {run.errorMessage && (
                    <p className="text-[9px] text-red-500 mt-1 truncate" title={run.errorMessage}>
                      {run.errorMessage}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NasSyncPanel;
